<template>
<q-page class="main">
  <q-card class="page-card">
    <BaseMap class="page-card__map"
             :geometry="project.json_polygon"
             auto-fit
             canFullscreen
             canZoom
             :canPan="false">
      <MapButton
          v-if="$can('updateCategories', project) && (!syncStatus.status || syncStatus.status === 'source_not_found')"
          tooltip="Project gebied aanpassen"
          icon="edit"
          @click="$goto('project.map.edit', {project_id: project.id})" />
    </BaseMap>
    <q-card-section class="page-card__main-content">
      <item-header :item="{...project.latest_version, ...project}" :reference-number="project.reference_number">
        <template v-if="syncStatus.status && !project.deleted_at">
          <StatusDot :status="syncStatus.status"
                     aria-labelledby="syncDescription"
                     class="q-mt-md" />
          <div v-if="syncStatus.status === 'up_to_date'" class="text-neutral-80 q-ml-sm">
            Project is up to date
          </div>
          <div v-else-if="syncStatus.status === 'updatable'" class="text-neutral-80 q-ml-sm">
            Project kan worden gesynchroniseerd
            <q-chip v-if="$can('update', project)" outline class="action-chip" icon="add" clickable
                    @click="synchronizeProjectWithExternal">
              Synchroniseer
            </q-chip>
          </div>
          <div v-else-if="syncStatus.status === 'ahead'" class="text-neutral-80 q-ml-sm">
            Project ligt voor op bron
          </div>
          <div v-else-if="syncStatus.status === 'source_not_found'" class="text-neutral-80 q-ml-sm">
            Projectbron kan niet worden gevonden
          </div>
        </template>
      </item-header>
      <common-banner v-if="project.deleted_at" type="warning" class="q-mb-lg">
        Dit project is gearchiveerd.
        <template v-slot:actions>
          <q-btn v-if="$can('restore', project)" color="warning" text-color="text-base" unelevated no-caps
                 class="q-mr-sm"
                 @click="restoreProject">
            Herstel het project
          </q-btn>
          <q-btn v-if="$can('delete-from-archive')"
                 color="negative" text-color="white" unelevated no-caps @click="forceDeleteProject">
            Verwijder defintief
          </q-btn>
        </template>
      </common-banner>
      <common-banner v-if="$can('update', project) && $isNew(project)" type="warning" class="q-mb-lg">
        Dit project is nog niet voorzien van een titel en omschrijving.
        Klik op de knop om deze aan te maken.
        Dit is nodig om gebruik te maken van de projectbeheermogelijkheden van het HIOR platform.
        <template v-slot:actions>
          <q-btn color="warning" text-color="text-base" unelevated no-caps @click="editProject">
            Bewerk
          </q-btn>
        </template>
      </common-banner>

      <item-info :item="project.latest_version" :users="project.users" />
      <item-description :description="project.latest_version.description" />
    </q-card-section>
    <q-card-section class="page-card__details">
      <expansion-element
          label="Projectfases"
          :items-count="phasesCount"
      >
        <phase-list :phases="project.phases"
                    clickable
                    @click="gotoPhase" />
      </expansion-element>
      <expansion-element
          v-if="currentPhase"
          label="Documenten"
          :items-count="documentsCount"
          showEmpty>
        <document-list
            :can-edit="$can('addFiles', project)"
            :documents="project.assets"
            :uploadUrl="$route('project.rule.add_file', [project.id, currentPhase.id])"
            :deleteUrl="$route('project.delete_file', project.id)" />
      </expansion-element>
      <expansion-element
          label="Categorieën"
          :items-count="categoriesCount"
      >
        <chip-list :items="project.categories" />
        <template v-slot:actions v-if="$can('updateCategories', project)">
          <flat-button label="Categorieën aanpassen" icon="edit"
                       @click="$goto('project.category.edit', {project_id: project.id})" />
        </template>
      </expansion-element>
      <expansion-element v-if="project.themes.length"
                         label="Thema's"
                         :items-count="project.themes.length"
      >
        <chip-list :items="project.themes" />
      </expansion-element>
      <expansion-element
          v-if="structuralObjectsEnabled"
          label="Inrichtingselementen"
          :items-count="objectsCount"
      >
        <object-group-list :items="objectGroups" :project="project" />
        <template v-slot:actions v-if="$can('updateObjects', project)">
          <flat-button label="Inrichtingselementen aanpassen" icon="edit"
                       @click="$goto('project.object.edit', {project_id: project.id})" />
        </template>
      </expansion-element>
    </q-card-section>
  </q-card>
  <sticky-top-bar>
    <template v-slot:actions>
      <flat-button label="Print richtlijnen" color="accent" icon="print" @click="printRules" />
    </template>
    <template v-slot:menu_items>
      <nav-list-item
          v-if="$can('manage-rules') && !$isNew(project)"
          label="Beheer flexibele richtlijnen"
          icon="view_timeline"
          action
          @click="addFlexibleRule"
      />
      <nav-list-item
          v-if="$can('addCustomRules', project) && !$isNew(project)"
          label="Voeg project-specifieke richtlijn toe"
          icon="playlist_add"
          action
          @click="addCustomRule"
      />
      <nav-list-item
          label="Exporteer richtlijnen"
          icon="note_add"
          action
          @click="exportRules"
      />
      <nav-list-item
          v-if="$can('destroy', project)"
          label="Archiveren"
          icon="inventory_2"
          action
          @click="onArchive"
      />
    </template>
  </sticky-top-bar>
  <sticky-bottom-bar>
    <template v-if="$isDesktop">
      <common-fab v-if="$can('update', project) && !$isNew(project)" icon="edit" label="Bewerken"
                  @click="editProject" />
    </template>
    <common-fab v-else>
      <template v-slot:actions>
        <fab-action
            v-if="$can('update', project) && !$isNew(project)"
            icon="edit"
            label="Bewerken"
            @click="editProject"
        />
        <fab-action
            v-if="$can('manage-rules') && !$isNew(project)"
            icon="view_timeline"
            label="Beheer flexibele richtlijnen"
            @click="addFlexibleRule"
        />
        <fab-action
            v-if="$can('addCustomRules', project) && !$isNew(project)"
            icon="playlist_add"
            label="Voeg project-specifieke richtlijn toe"
            @click="addCustomRule"
        />
        <fab-action
            icon="print"
            label="Print richtlijnen"
            @click="printRules"
        />
        <fab-action
            icon="note_add"
            label="Exporteer richtlijnen"
            @click="exportRules"
        />
        <fab-action
            v-if="$can('destroy', project)"
            icon="inventory_2"
            label="Archiveren"
            @click="onArchive"
        />
      </template>
    </common-fab>
  </sticky-bottom-bar>
</q-page>
</template>

<script>
import ChipList from '~HIOR/components/lists/ChipList'
import DocumentList from '~HIOR/components/lists/DocumentList'
import PhaseList from '~HIOR/components/lists/PhaseList'
import ObjectGroupList from '~HIOR/components/lists/ObjectGroupList'
import { phaseStatus } from '~HIOR/constants'
import { projectMixin } from '~HIOR/mixins'
import { promiseTimeout } from '@vueuse/core'
import SynchroniseProjectDialog from '~HIOR/components/dialog/SynchroniseProjectDialog'

export default {
  components: {
    ObjectGroupList,
    ChipList,
    DocumentList,
    PhaseList,
  },
  mixins: [projectMixin],
  props: {
    project: {
      type: Object,
      required: false,
    },
    phase: {
      type: Object,
      required: false,
    },
    phases: {
      type: Array,
      default: () => [],
    },
    objectGroups: {
      type: Object,
      default: () => {
        return {}
      },
    },
    syncStatus: {
      type: Object,
      default: () => ({}),
    },
    ruleVersions: {
      type: Array,
      default: () => [],
    },
    showIrrelevant: {
      type: Boolean,
      default: true,
    },
    structuralObjectsEnabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    categoriesCount () {
      return this.project.categories ? this.project.categories.length : null
    },
    phasesCount () {
      return this.project.phases ? this.project.phases.length : null
    },
    documentsCount () {
      return this.project.assets ? this.project.assets.length : null
    },
    objectsCount () {
      const keys = Object.keys(this.objectGroups)

      if (Object.keys(this.objectGroups).length === 0) {
        return null
      }

      let sum = 0
      keys.forEach((key) => {
        sum += this.objectGroups[key].length
      })

      return sum
    },
    currentPhase () {
      return this.project.phases.find(phase => phase.status === phaseStatus.STATUS_ACTIVE)
    },
  },
  methods: {
    editProject () {
      this.$goto('project.edit', { project: this.project.id })
    },
    addFlexibleRule () {
      this.$goto('flexible-rule.index', { project_id: this.project.id })
    },
    addCustomRule () {
      this.$goto('custom-rule.create', { project_id: this.project.id })
    },
    printRules () {
      this.$openWindow('project.print', { project_id: this.project.id, type: 'pdf' })
    },
    exportRules () {
      this.$openWindow('project.print', { project_id: this.project.id, type: 'excel' })
    },
    gotoPhase (phase) {
      this.$goto('project.rule.index', { project_id: this.project.id, phase_id: phase.id })
    },
    restoreProject () {
      this.$goto('project.restore', { project_id: this.project.id })
    },
    synchronizeProjectWithExternal () {
      this.$q.dialog({
        component: SynchroniseProjectDialog,
        componentProps: {
          syncStatus: this.syncStatus,
          projectId: this.project.id,
        },
      })
    },
    async onArchive () {
      if (!await this.$confirm(
          {
            title: 'Project archiveren',
            messages: ['Weet u zeker dat u dit project wilt archiveren?'],
          },
      )) {
        return
      }
      try {
        await axios.delete(this.$route('project.destroy', { project_id: this.project.id }))
        await this.$toast('success', 'Het project is gearchiveerd')
      } catch (error) {
        console.error(error)
        this.$toast('error', 'Er is iets mis gegaan')
        return
      }
      await promiseTimeout(1000)
      this.$goto('project.index')
    },
    async forceDeleteProject () {
      if (!await this.$confirm(
          {
            title: 'Project vernietigen',
            messages: ['Weet u zeker dat u dit project definitief wilt verwijderen? Het project is dan niet meer vindbaar en alle correspondentie en bestanden worden verwijderd. Deze actie kan niet worden teruggedraaid.'],
          },
      )) {
        return
      }
      try {
        await axios.delete(this.$route('project.force-delete', { project_id: this.project.id }))
        this.$toast('success', 'Het project is verwijderd')
      } catch (error) {
        console.error(error)
        this.$toast('error', 'Er is iets mis gegaan')
        return
      }
      await promiseTimeout(1000)
      this.$goto('project.index')
    },
  },
  mounted () {
    console.log()
  }
  ,
}
</script>
